// The pure half of a failed purchase's recovery — what the console reads from the roster
// row (`nexaPurchase.recovery`, live over the WS) and from the per-group view. Holds no
// language: every string comes from the translator handed in (keys under `pnr:recovery.*`).

import type { DisruptionPnrGroupRow, NexaPurchase } from '@/lib/disruptions/types';
import type { CandidateWhy, IRecoveryCandidate, IRecoveryView, RecoveryState } from './types';

export type RecoveryT = (key: string, opts?: Record<string, unknown>) => string;

/** Where the card reads the group's purchase: a recovery state, a refused purchase that
 *  predates recovery (`REVIEW`), a purchase running (`BUYING`), or none. */
export type PurchasePhase = Exclude<RecoveryState, 'NONE'> | 'REVIEW' | 'BUYING';

const NEXA_BUYS = new Set(['duffel', 'atlas']);

/** The flight is one Nexa buys from a supplier (Duffel / Atlas), not the airline's own issue. */
export function nexaBuys(row: Pick<DisruptionPnrGroupRow, 'selectedProvider' | 'nexaPurchase'>): boolean {
  return NEXA_BUYS.has(row.selectedProvider ?? '') || !!row.nexaPurchase;
}

export function purchasePhase(purchase: NexaPurchase | undefined): PurchasePhase | null {
  if (!purchase) return null;
  // The recovery first: with only a recovery, `status` is a stand-in.
  const state = purchase.recovery?.state;
  if (state && state !== 'NONE') return state;
  if (purchase.status === 'NEEDS_REVIEW') return 'REVIEW';
  if (purchase.status === 'QUEUED' || purchase.status === 'BUYING') return 'BUYING';
  return null;
}

/** The group has a recovery to read (a state other than NONE, or a refused purchase). */
export function hasRecovery(row: Pick<DisruptionPnrGroupRow, 'nexaPurchase'>): boolean {
  const phase = purchasePhase(row.nexaPurchase);
  return phase !== null && phase !== 'BUYING';
}

/**
 * The re-offered flight the passenger accepted (PO 2026-11-03: «¿por qué aceptó el viejo y no
 * el nuevo?» — they had accepted the new one; the card paired the old flight with the new
 * answer). The episode's `accepted` stays the flight that first failed; a later answer to the
 * re-offer is about `reoffer.optionUrn`. Undefined while no answer came after the re-offer.
 */
export function reacceptedCandidate(
  view: Pick<IRecoveryView, 'reoffer' | 'candidates'> | undefined,
  answer: { response: 'ACCEPTED' | 'DECLINED'; at: string } | undefined,
): IRecoveryCandidate | undefined {
  return answer?.response === 'ACCEPTED' ? reofferAnswered(view, answer) : undefined;
}

/** The re-offered flight the passenger's last answer is about (it came after the re-offer). */
function reofferAnswered(
  view: Pick<IRecoveryView, 'reoffer' | 'candidates'> | undefined,
  answer: { at: string } | undefined,
): IRecoveryCandidate | undefined {
  const re = view?.reoffer;
  if (!re || !answer) return undefined;
  const at = Date.parse(answer.at);
  const sent = Date.parse(re.sentAt);
  if (!Number.isFinite(at) || !Number.isFinite(sent) || at < sent) return undefined;
  return view.candidates.find((c) => c.optionUrn === re.optionUrn);
}

/**
 * Which flight the passenger's last answer was about: the re-offer when it came after it, else
 * the flight they accepted first (a recovery's `accepted`), else the group's selected flight.
 */
export function answeredFlight(
  view: Pick<IRecoveryView, 'reoffer' | 'candidates' | 'accepted'> | undefined,
  answer: { at: string } | undefined,
  selected?: string | undefined,
): string | undefined {
  if (!answer) return undefined;
  return reofferAnswered(view, answer)?.flightNumber ?? view?.accepted?.flightNumber ?? selected;
}

/**
 * Why the accepted flight's purchase failed, as the operator should read it: `DEPARTED` when the
 * flight had already left at the attempt (rebooking classifies it OFFER_GONE · FLIGHT_DEPARTED —
 * «Duffel lo agotó» would be untrue), else the attempt's own reason.
 */
export function failureReasonOf(
  view: Pick<IRecoveryView, 'failedAttempt' | 'accepted'> | undefined,
): string | undefined {
  const f = view?.failedAttempt;
  if (!f) return undefined;
  const a = view.accepted;
  if (a && a.optionUrn === f.optionUrn && Date.parse(a.departAt) <= Date.parse(f.at)) return 'DEPARTED';
  return f.reason;
}

/** «Duffel», «Atlas» — a supplier as the operator names it. */
export function providerName(provider: string | undefined): string {
  if (!provider) return '—';
  if (provider === 'duffel') return 'Duffel';
  if (provider === 'atlas') return 'Atlas';
  return provider.charAt(0).toUpperCase() + provider.slice(1);
}

/** «2 h 30 min», «45 min», «3 h». */
export function formatDelay(minutes: number, t: RecoveryT): string {
  const m = Math.max(0, Math.round(minutes));
  const h = Math.floor(m / 60);
  const rest = m % 60;
  if (h === 0) return t('recovery.delay.minutes', { m: rest });
  if (rest === 0) return t('recovery.delay.hours', { h });
  return t('recovery.delay.hoursMinutes', { h, m: rest });
}

/** What keeps a candidate from being offered or bought at all — RB1's `offerable()`: it leaves
 *  too soon, it is over the price cap, or it has no room for the group. */
const BLOCKING: readonly CandidateWhy[] = ['TOO_SOON', 'OVER_CAP', 'NO_CAPACITY'];

/** The candidate's flight already left (a list from an earlier search read later): never offered. */
export function departed(c: IRecoveryCandidate, now: number = Date.now()): boolean {
  const t = Date.parse(c.departAt);
  return Number.isFinite(t) && t <= now;
}

/** A candidate that can be offered or bought: not yet departed, in time, within the cap, with
 *  room for the group. */
export function offerable(c: IRecoveryCandidate, now: number = Date.now()): boolean {
  return !departed(c, now) && !c.why.some((w) => BLOCKING.includes(w));
}

/** The candidate the console proposes first: an equivalent one, else the earliest arrival. */
export function bestCandidate(view: IRecoveryView | undefined, now: number = Date.now()): IRecoveryCandidate | undefined {
  const list = (view?.candidates ?? []).filter((c) => offerable(c, now));
  return (
    list.find((c) => c.sameAsAccepted) ??
    list.find((c) => c.equivalent) ??
    [...list].sort((a, b) => a.arrivalDelayMinutes - b.arrivalDelayMinutes)[0]
  );
}

/** What a candidate means for the operator: buy it (equivalent), offer it, it costs a night, or
 *  it is blocked (departed, too soon, over the cap, no room). */
export type CandidateVerdict = 'equivalent' | 'offer' | 'costly' | 'blocked';

export function candidateVerdict(c: IRecoveryCandidate, now: number = Date.now()): CandidateVerdict {
  if (!offerable(c, now)) return 'blocked';
  if (c.equivalent || c.sameAsAccepted) return 'equivalent';
  if (c.why.includes('OTHER_DAY') || c.why.includes('EXTRA_HOTEL_NIGHT')) return 'costly';
  return 'offer';
}

/** One line for a candidate: «llega +2 h 30 min · hay que ofrecerlo», «otro día · suma una noche de hotel». */
export function candidateLine(c: IRecoveryCandidate, t: RecoveryT, now: number = Date.now()): string {
  const verdict = candidateVerdict(c, now);
  // Blocked: only what blocks it («ya salió», «sale demasiado pronto», «supera el tope de precio», …).
  if (verdict === 'blocked') {
    if (departed(c, now)) return t('recovery.why.DEPARTED');
    return c.why.filter((w) => BLOCKING.includes(w)).map((w) => t(`recovery.why.${w}`)).join(' · ');
  }
  if (c.sameAsAccepted) return t('recovery.candidate.sameAsAccepted');
  if (verdict === 'equivalent') {
    return c.arrivalDelayMinutes > 0
      ? t('recovery.candidate.equivalentLater', { delay: formatDelay(c.arrivalDelayMinutes, t) })
      : t('recovery.candidate.equivalent');
  }
  const parts = c.why.map((w) =>
    w === 'ARRIVES_LATER' ? t('recovery.why.ARRIVES_LATER', { delay: formatDelay(c.arrivalDelayMinutes, t) }) : t(`recovery.why.${w}`),
  );
  if (parts.length === 0 && c.arrivalDelayMinutes > 0) {
    parts.push(t('recovery.why.ARRIVES_LATER', { delay: formatDelay(c.arrivalDelayMinutes, t) }));
  }
  if (verdict === 'offer') parts.push(t('recovery.candidate.mustOffer'));
  return parts.join(' · ');
}

/** The traveler fields Duffel names in a refusal — the ones the console can word. */
const TRAVELER_FIELDS: ReadonlySet<string> = new Set([
  'born_on',
  'phone_number',
  'email',
  'given_name',
  'family_name',
  'gender',
  'title',
]);

/** The failed attempt's reason as the band's opening clause («Duffel agotó el vuelo que aceptaron.»). */
export function failureClause(
  reason: string | undefined,
  provider: string,
  t: RecoveryT,
  field?: string,
  /** Under the accepted flight itself: the clause no longer names it («…el vuelo que aceptaron»). */
  short = false,
): string {
  if (short && (reason === 'OFFER_GONE' || reason === 'PRICE_CHANGED' || reason === 'DEPARTED')) {
    return t(`recovery.failureShort.${reason}`, { provider });
  }
  switch (reason) {
    case 'DEPARTED':
      return t('recovery.failure.DEPARTED', { provider });
    case 'OFFER_GONE':
    case 'PRICE_CHANGED':
    case 'UNCONFIRMED':
    case 'PROVIDER_ERROR':
      return t(`recovery.failure.${reason}`, { provider });
    case 'INVALID_TRAVELER_DATA':
      // The supplier's field (`born_on`) is a vendor code: worded when known, else «a passenger
      // detail». Never the code, never the value.
      return field && TRAVELER_FIELDS.has(field)
        ? t('recovery.failure.INVALID_TRAVELER_DATA_field', { provider, field: t(`recovery.field.${field}`) })
        : t('recovery.failure.INVALID_TRAVELER_DATA', { provider });
    default:
      return t(short ? 'recovery.failureShort.other' : 'recovery.failure.other', { provider });
  }
}
